import { type DbSafetyInfo, fetchDbSafety } from "./DbSafetyService";

import { sanitizePublicMessage } from "@/utils/errors/publicErrorContract";
import {
  CORRELATION_ID_HEADER,
  generateCorrelationId,
} from "@/utils/logging/correlationId";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { getBackendOrigin } from "@/utils/network/getBackendOrigin";

export type StripeRuntimeMode = "live" | "test" | "unknown";
export type StripeConfiguredMode = "live" | "test" | "unknown";

export interface StripeSafetyInfo {
  runtimeMode: StripeRuntimeMode;
  configuredMode: StripeConfiguredMode;
  modeMatchesConfiguration: boolean | null;
  checkedAtIso?: string;
}

export interface EnvironmentSafetyInfo {
  dbSafety: DbSafetyInfo | null;
  stripeSafety: StripeSafetyInfo | null;
}

interface ApiHealthResponse {
  environmentSafety?: {
    db?: ApiHealthDbSafetyResponse;
    stripe?: {
      runtimeMode?: unknown;
      configuredMode?: unknown;
      modeMatchesConfiguration?: unknown;
      checkedAtIso?: unknown;
    };
  };
}

interface ApiHealthDbSafetyResponse {
  isProduction?: unknown;
  dbHost?: unknown;
  dbName?: unknown;
  checkedAtIso?: unknown;
}

const toSafeErrorMessage = (error: unknown): string =>
  sanitizePublicMessage(error instanceof Error ? error.message : String(error));

const resolveCorrelationIdFromResponse = (
  response: Response,
  fallbackCorrelationId: string,
): string => {
  const responseCorrelationId =
    response.headers.get("x-correlation-id") ??
    response.headers.get("X-Correlation-ID");

  const normalizedCorrelationId = responseCorrelationId?.trim();
  if (!normalizedCorrelationId) {
    return fallbackCorrelationId;
  }

  return normalizedCorrelationId;
};

const buildApiHealthUrl = (): string => {
  const endpoint = "/api-health";
  const backendOrigin = getBackendOrigin();
  if (!backendOrigin) {
    return endpoint;
  }

  return `${backendOrigin}${endpoint}`;
};

const normalizeStripeMode = (raw: unknown): StripeRuntimeMode | null => {
  if (typeof raw !== "string") {
    return null;
  }

  const normalized = raw.trim().toLowerCase();
  if (
    normalized === "test" ||
    normalized === "live" ||
    normalized === "unknown"
  ) {
    return normalized;
  }

  return null;
};

const normalizeConfiguredMode = (raw: unknown): StripeConfiguredMode | null => {
  if (typeof raw !== "string") {
    return null;
  }

  const normalized = raw.trim().toLowerCase();
  if (
    normalized === "test" ||
    normalized === "live" ||
    normalized === "unknown"
  ) {
    return normalized;
  }

  return null;
};

const normalizeApiHealthDbSafety = (
  db: ApiHealthDbSafetyResponse | undefined,
): DbSafetyInfo | null => {
  if (typeof db?.isProduction !== "boolean") {
    return null;
  }

  return {
    isProduction: db.isProduction,
    dbHost: typeof db.dbHost === "string" ? db.dbHost : null,
    dbName: typeof db.dbName === "string" ? db.dbName : null,
    checkedAtIso:
      typeof db.checkedAtIso === "string" ? db.checkedAtIso : undefined,
  };
};

/**
 * Reads backend `/api-health` and extracts the badge diagnostics payload.
 * If production-mode health hardening strips DB diagnostics from a staging
 * response, fall back to the origin-guarded `/db-safety` endpoint so the badge
 * still shows which database is connected.
 */
export const fetchEnvironmentSafety =
  async (): Promise<EnvironmentSafetyInfo | null> => {
    const logger = FrontendLoggerFactory.getLogger().withComponent(
      "EnvironmentSafetyService",
    );
    const correlationId = generateCorrelationId();
    const requestLogger = logger.withCorrelationId(correlationId);
    const url = buildApiHealthUrl();

    try {
      requestLogger.debug("Checking environment safety from api-health", {
        operation: "environment_safety_start",
        url,
      });

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          [CORRELATION_ID_HEADER]: correlationId,
        },
      });

      const responseCorrelationId = resolveCorrelationIdFromResponse(
        response,
        correlationId,
      );
      const responseLogger = requestLogger.withCorrelationId(
        responseCorrelationId,
      );

      if (!response.ok) {
        responseLogger.warn("Environment safety check returned non-OK status", {
          operation: "environment_safety_non_ok",
          url,
          status: response.status,
          statusText: response.statusText,
        });
        return null;
      }

      const raw = (await response.json()) as ApiHealthResponse;
      const db = raw.environmentSafety?.db;
      const stripe = raw.environmentSafety?.stripe;

      const dbSafety =
        normalizeApiHealthDbSafety(db) ?? (await fetchDbSafety());

      const runtimeMode = normalizeStripeMode(stripe?.runtimeMode);
      const configuredMode = normalizeConfiguredMode(stripe?.configuredMode);
      const modeMatchesConfiguration =
        typeof stripe?.modeMatchesConfiguration === "boolean"
          ? stripe.modeMatchesConfiguration
          : null;
      const stripeSafety: StripeSafetyInfo | null =
        runtimeMode && configuredMode
          ? {
              runtimeMode,
              configuredMode,
              modeMatchesConfiguration,
              checkedAtIso:
                typeof stripe?.checkedAtIso === "string"
                  ? stripe.checkedAtIso
                  : undefined,
            }
          : null;

      responseLogger.info("Environment safety check completed", {
        operation: "environment_safety_success",
        url,
        hasDbSafety: Boolean(dbSafety),
        hasStripeSafety: Boolean(stripeSafety),
      });

      return {
        dbSafety,
        stripeSafety,
      };
    } catch (error) {
      requestLogger.warn(
        "Environment safety check failed with network/runtime error",
        {
          operation: "environment_safety_error",
          url,
          error: toSafeErrorMessage(error),
        },
      );
      return null;
    }
  };
